import { useEffect, useState } from 'react';
import { api, type AircraftStats, type FlightSummary } from '../lib/api';
import { fmt } from '../lib/altitude';

interface FlightHistoryProps {
  hex: string;
  onReplay: (flightId: number) => void;
}

/** Flugliste + Aggregatstatistiken eines Fliegers (Daten aus dem Cron-Tracking). */
export default function FlightHistory({ hex, onReplay }: FlightHistoryProps) {
  const [flights, setFlights] = useState<FlightSummary[] | null>(null);
  const [stats, setStats] = useState<AircraftStats | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setFlights(null);
    setStats(null);
    Promise.all([api.flights(hex, ctrl.signal), api.stats(hex, ctrl.signal)])
      .then(([f, s]) => {
        setFlights(f);
        setStats(s);
      })
      .catch(() => setFlights([]));
    return () => ctrl.abort();
  }, [hex]);

  if (flights === null) {
    return (
      <div className="flight-list" aria-busy="true" aria-label="Lade Historie">
        <div className="skeleton" style={{ height: '1.7rem' }} />
        <div className="skeleton" style={{ height: '1.7rem' }} />
        <div className="skeleton" style={{ height: '1.7rem' }} />
      </div>
    );
  }
  if (flights.length === 0) {
    return (
      <p className="muted history-note">
        Noch keine aufgezeichneten Flüge — auf der Watchlist wird dieser Flieger automatisch
        getrackt.
      </p>
    );
  }

  const totals = stats?.totals;
  const monthNow = stats?.per_month[0];

  return (
    <div className="flight-history">
      {totals && totals.flights > 0 && (
        <div className="history-stats mono">
          <span title="Aufgezeichnete Flüge">{totals.flights} FLG</span>
          <span title="Gesamtdistanz">{fmt.distance(totals.total_distance_km)}</span>
          <span title="Gesamtflugzeit">{fmt.duration(totals.total_duration_s)}</span>
          {monthNow && <span title={`Flüge im ${monthNow.month}`}>{monthNow.flights}/Mon</span>}
        </div>
      )}

      {stats && stats.top_routes.length > 0 && (
        <div className="top-routes mono">
          {stats.top_routes.slice(0, 3).map((r) => (
            <span key={`${r.origin_icao}-${r.dest_icao}`}>
              {r.origin_icao}→{r.dest_icao} ×{r.count}
            </span>
          ))}
        </div>
      )}

      <ul className="flight-list">
        {flights.map((f) => (
          <li key={f.id}>
            <button
              className="flight-row"
              onClick={() => onReplay(f.id)}
              title="Track auf der Karte abspielen"
            >
              <span className="flight-date mono">{fmt.date(f.started_at)}</span>
              <span className="flight-route mono">
                {f.origin_icao && f.dest_icao
                  ? `${f.origin_icao}→${f.dest_icao}`
                  : (f.callsign ?? '—')}
              </span>
              <span className="flight-stats mono">
                {f.status === 'open' ? 'LÄUFT' : `${fmt.duration(f.duration_s)} · ${fmt.distance(f.distance_km)}`}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
